import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel, summarizeGameState } from "@/utils/debugUtils";

/**
 * Generates a debug summary of the current game state
 */
export const generateGameStateSummary = async (
  gameId: string | null,
  roundNumber: number,
  botIsThinking: boolean,
  botHasChosenWord: boolean,
  userHasSubmitted: boolean,
  processingComplete: boolean
) => {
  if (!gameId) return;
  
  try {
    await summarizeGameState(
      supabase,
      gameId,
      roundNumber,
      botIsThinking,
      botHasChosenWord,
      userHasSubmitted,
      processingComplete
    );
  } catch (error) {
    debugLog(DebugLevel.ERROR, "wordSubmission", "Error generating game state summary:", error);
  }
};

/**
 * Creates state setters for use in the hooks
 */
export const createStateSetters = (
  setRounds: (rounds: any) => void,
  setUsedWords: (usedWords: any) => void,
  setCurrentWord: (word: string) => void,
  setIsRevealed: (isRevealed: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  setUserHasSubmitted: (hasSubmitted: boolean) => void,
  setProcessingComplete: (complete: boolean) => void
) => {
  return {
    setRounds,
    setUsedWords,
    setCurrentWord,
    setIsRevealed,
    setBotHasChosenWord,
    setBotIsThinking,
    setShouldAutoFocus,
    setGameOver,
    setConvergenceRound,
    setGameStatus,
    setUserHasSubmitted,
    setProcessingComplete
  };
};

/**
 * Handles the case when bot has not yet chosen its word
 */
export const handleBotStillThinking = (
  setWaitingForBotWord: (waiting: boolean) => void,
  setBotIsThinking: (thinking: boolean) => void,
  setProcessingComplete: (complete: boolean) => void,
  setIsSubmitting: (submitting: boolean) => void
) => {
  debugLog(DebugLevel.INFO, "wordSubmission", "User submitted word but bot is still thinking. Setting waitingForBotWord to true.");
  
  // Set the waiting flag so we can process when bot finishes
  setWaitingForBotWord(true);
  
  // Make sure botIsThinking is true to keep the checkmark showing
  setBotIsThinking(true);
  // Reset processing complete since we're waiting for the bot
  setProcessingComplete(true);
  setIsSubmitting(false);
};
